// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Divider, Popover, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// api
import { useGetSensorConnectionInfoByIdQuery } from '@/api/recalc/endpoints/sensorsApiSlice';

// shared components
import StyledBadgeWrapper from '@/components/experimental/StyledBadge';

// relative
import { deriveSensorStatus } from '../utils/deriveSensorStatus';
import SensorRssiSparkline from './SensorRssiSparkline';

import { DateTime } from 'luxon';

// ─── Timestamp row ────────────────────────────────────────────────────────────
function TimestampRow({ label, isoString }) {
  if (!isoString) return null;
  const formatted = DateTime.fromISO(isoString, { zone: 'Europe/Copenhagen' })
    .setLocale('da')
    .toFormat('dd MMM yyyy HH:mm:ss');
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="caption" fontFamily="monospace">
        {formatted}
      </Typography>
    </Stack>
  );
}

// ─── Status panel (reusable — used in Popover and on the detail page) ────────
/**
 * Renders the full status content without any Popover wrapper.
 * Pass `alwaysFetch` to skip the lazy-load guard (e.g. on a detail page).
 */
export function SensorStatusPanel({ sensor }) {
  const { t } = useTranslation('units-gateways-sensors');
  const theme = useTheme();

  const status = deriveSensorStatus(sensor);
  const color = theme.palette.status[status.toLowerCase()];

  const connections = sensor?.LatestSensorActivities ?? [];
  const bestConnection = connections.reduce(
    (best, c) =>
      !best || new Date(c.LastActivity) > new Date(best.LastActivity)
        ? c
        : best,
    null,
  );

  const sparklineTimestamp = React.useMemo(
    () => DateTime.utc().minus({ days: 3 }).toISO(),
    [],
  );
  const { data: connectionInfo } = useGetSensorConnectionInfoByIdQuery(
    { sensorId: sensor?.SensorId, timestamp: sparklineTimestamp },
    { skip: !sensor?.SensorId },
  );

  const statusLabel = {
    Online: t('sensors.statusOnline', { defaultValue: 'Online' }),
    Degraded: t('sensors.statusDegraded', {
      defaultValue: 'Online — data may be stale',
    }),
    Offline: t('sensors.statusOffline', { defaultValue: 'Offline' }),
  }[status];

  return (
    <Stack spacing={0}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color }}>
          {statusLabel}
        </Typography>
      </Stack>

      {status === 'Degraded' && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: 'block', mb: 1 }}
        >
          {t('sensors.degradedHint', {
            defaultValue:
              'The sensor is active, but the last recorded data deviates by more than 1 hour from the latest connection activity.',
          })}
        </Typography>
      )}

      <Divider sx={{ mb: 1 }} />

      {/* Timestamps */}
      <Stack spacing={0.5} sx={{ mb: 1 }}>
        <TimestampRow
          label={t('sensors.lastActivity', { defaultValue: 'Last activity' })}
          isoString={bestConnection?.LastActivity}
        />
        <TimestampRow
          label={t('sensors.lastRecorded', {
            defaultValue: 'Data last recorded',
          })}
          isoString={sensor?.DataLastRecorded}
        />
      </Stack>

      {/* Connection details */}
      {bestConnection && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            {t('sensors.connectionDetails', {
              defaultValue: 'Connection details',
            })}
          </Typography>
          <Stack spacing={0.5}>
            {bestConnection.ReceivedSignalStrengthIndication != null && (
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="caption" color="text.secondary">
                  {t('sensors.rssi', { defaultValue: 'RSSI' })}
                </Typography>
                <Typography variant="caption" fontFamily="monospace">
                  {bestConnection.ReceivedSignalStrengthIndication} dBm
                </Typography>
              </Stack>
            )}
            {bestConnection.LinkQualityIndication != null && (
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="caption" color="text.secondary">
                  {t('sensors.lqi', { defaultValue: 'LQI' })}
                </Typography>
                <Typography variant="caption" fontFamily="monospace">
                  {bestConnection.LinkQualityIndication}
                </Typography>
              </Stack>
            )}
            {bestConnection.GatewayId != null && (
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="caption" color="text.secondary">
                  {t('sensors.gatewayId', { defaultValue: 'Gateway' })}
                </Typography>
                <Typography variant="caption" fontFamily="monospace">
                  {bestConnection.GatewayId}
                </Typography>
              </Stack>
            )}
          </Stack>
        </>
      )}

      {/* RSSI Sparkline */}
      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
        {t('sensors.rssiHistory', {
          defaultValue: 'Signal history (3 days)',
        })}
      </Typography>
      <SensorRssiSparkline dataPoints={connectionInfo} height={56} />
    </Stack>
  );
}

// ─── Main cell ────────────────────────────────────────────────────────────────
export default function SensorStatusCell({ sensor }) {
  const { t } = useTranslation('units-gateways-sensors');
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();

  const status = deriveSensorStatus(sensor);
  const color = theme.palette.status[status.toLowerCase()];

  const statusLabel = {
    Online: t('sensors.statusOnline', { defaultValue: 'Online' }),
    Degraded: t('sensors.statusDegraded', {
      defaultValue: 'Online — data may be stale',
    }),
    Offline: t('sensors.statusOffline', { defaultValue: 'Offline' }),
  }[status];

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = (e) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          maxWidth: 75,
          minWidth: 75,
        }}
      >
        <StyledBadgeWrapper color={color} enableRipple={false} />
        <Typography variant="body2">{statusLabel}</Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, minWidth: 280, maxWidth: 360 } } }}
      >
        {open && <SensorStatusPanel sensor={sensor} />}
      </Popover>
    </>
  );
}
